import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Button,
  type Column,
  EmptyState,
  Modal,
  PageHead,
  Pill,
  Select,
  Table,
  Tabs,
  type TabItem,
} from "@/components/common";
import { Guard } from "@/auth/Guard";
import { useActiveWorkspace } from "@/contexts/WorkspaceContext";
import { useClients } from "@/hooks/useClients";
import {
  useAddContractTese,
  useAddIndicacao,
  useAttachSignature,
  useContract,
  useDeleteContract,
  useRemoveContractTese,
  useRemoveIndicacao,
  useTransitionContract,
  useUpdateContract,
  useUpdateIndicacao,
} from "@/hooks/useContracts";
import { useContractTemplates } from "@/hooks/useContractTemplates";
import { useMembers } from "@/hooks/useInvitations";
import { useTeses } from "@/hooks/useTeses";
import { extractErrorMessage } from "@/services/axiosConfig";
import type {
  ContractIndicacao,
  ContractStatus,
  ContractTeseLink,
  IndicacaoTipo,
  OperatorType,
  SignatureMethod,
} from "@/types/contract";
import { formatDate, formatPct } from "@/utils/fmt";
import { fractionToPct, pctToFraction } from "@/utils/pct";
import { CascadePreview } from "./ContractWizard";
import { computeCascade } from "./cascade";
import "./Contracts.css";

const STATUS_LABEL: Record<ContractStatus, string> = {
  draft: "Rascunho",
  active: "Ativo",
  paused: "Pausado",
  finished: "Encerrado",
  canceled: "Cancelado",
};

const STATUS_TONE: Record<ContractStatus, "diag" | "ativa" | "pausada" | "onb" | "alerta"> = {
  draft: "diag",
  active: "ativa",
  paused: "pausada",
  finished: "onb",
  canceled: "alerta",
};

const OPERATOR_LABEL: Record<OperatorType, string> = {
  tributo_devido: "Tributo Devido",
  organization: "Organização",
  workspace: "Workspace",
};

// Valid next states per the backend state machine.
const NEXT_STATES: Record<ContractStatus, ContractStatus[]> = {
  draft: ["active", "canceled"],
  active: ["paused", "finished", "canceled"],
  paused: ["active", "finished", "canceled"],
  finished: [],
  canceled: [],
};


// Fração ("0.0615") → percent limpo ("6.15"), sem artefatos de ponto flutuante
// como "6.149999999999999". Vazio/inválido → "".
function fractionToPctClean(fraction: string | null | undefined): string {
  const pct = fractionToPct(fraction);
  if (pct == null) return "";
  return String(Number(Number(pct).toFixed(6)));
}

type DetailTab = "visao" | "teses" | "indicacoes" | "assinatura";

export function ContractDetail() {
  const { id } = useParams<{ id: string }>();
  const contractId = id ? Number(id) : null;
  const { activeWorkspaceId } = useActiveWorkspace();
  const navigate = useNavigate();
  const [tab, setTab] = useState<DetailTab>("visao");
  const [editing, setEditing] = useState(false);

  const { data: contract, isLoading } = useContract(activeWorkspaceId, contractId);
  const { data: clients = [] } = useClients(activeWorkspaceId);
  const { data: templates = [] } = useContractTemplates(activeWorkspaceId);
  const deleteContract = useDeleteContract(activeWorkspaceId ?? 0);

  if (activeWorkspaceId == null) {
    return (
      <>
        <PageHead title="Contrato" />
        <EmptyState icon="file-text" title="Selecione um workspace" />
      </>
    );
  }

  if (!contract) {
    return (
      <>
        <PageHead title="Contrato" actions={<BackButton />} />
        <EmptyState
          icon="file-text"
          title={isLoading ? "Carregando…" : "Contrato não encontrado"}
        />
      </>
    );
  }

  const clientName =
    contract.client_id == null
      ? "Contrato do workspace (TD)"
      : (clients.find((c) => c.id === contract.client_id)?.legal_name ??
        `Cliente #${contract.client_id}`);
  const templateName =
    contract.template_id != null
      ? (templates.find((t) => t.id === contract.template_id)?.name ?? "—")
      : "—";

  const tabs: TabItem<DetailTab>[] = [
    { id: "visao", label: "Visão" },
    { id: "teses", label: "Teses", count: contract.teses.length },
    { id: "indicacoes", label: "Indicações", count: contract.indicacoes.length },
    { id: "assinatura", label: "Assinatura" },
  ];

  function BackButton() {
    return (
      <Button variant="ghost" iconLeft="arrow-left" onClick={() => navigate("/contratos")}>
        Voltar
      </Button>
    );
  }

  return (
    <>
      <PageHead
        title={clientName}
        subtitle={`Contrato #${contract.id}`}
        actions={
          <>
            <BackButton />
            <StatusActions
              workspaceId={activeWorkspaceId}
              contractId={contract.id}
              status={contract.status}
            />
            {contract.status !== "finished" && contract.status !== "canceled" && (
              <Guard permission="contracts.write">
                <Button variant="ghost" iconLeft="settings" onClick={() => setEditing(true)}>
                  Editar
                </Button>
              </Guard>
            )}
            <Guard permission="contracts.delete">
              <Button
                variant="danger"
                iconLeft="x"
                disabled={deleteContract.isPending}
                onClick={() => {
                  if (confirm("Excluir este contrato?")) {
                    void deleteContract
                      .mutateAsync(contract.id)
                      .then(() => navigate("/contratos"));
                  }
                }}
              >
                Excluir
              </Button>
            </Guard>
          </>
        }
      />

      <div className="emp-toolbar">
        <Pill tone={STATUS_TONE[contract.status]}>{STATUS_LABEL[contract.status]}</Pill>
      </div>

      <Tabs items={tabs} active={tab} onChange={setTab} />

      {tab === "visao" && <VisaoTab contract={contract} templateName={templateName} />}
      {tab === "teses" && (
        <TesesTab workspaceId={activeWorkspaceId} contractId={contract.id} links={contract.teses} />
      )}
      {tab === "indicacoes" && (
        <IndicacoesTab
          workspaceId={activeWorkspaceId}
          contractId={contract.id}
          indicacoes={contract.indicacoes}
        />
      )}
      {tab === "assinatura" && (
        <AssinaturaTab workspaceId={activeWorkspaceId} contract={contract} />
      )}

      {editing && (
        <EditContractModal
          workspaceId={activeWorkspaceId}
          contract={contract}
          onClose={() => setEditing(false)}
        />
      )}
    </>
  );
}

// ─── Editar (datas / fees / documento modelo) ──────────────────────────────

function EditContractModal({
  workspaceId,
  contract,
  onClose,
}: {
  workspaceId: number;
  contract: import("@/types/contract").ContractDetail;
  onClose: () => void;
}) {
  const update = useUpdateContract(workspaceId, contract.id);
  const [dataInicio, setDataInicio] = useState(contract.data_inicio);
  const [validade, setValidade] = useState(contract.validade);
  const [successFeePct, setSuccessFeePct] = useState(fractionToPctClean(contract.success_fee_pct));
  const [tdSharePct, setTdSharePct] = useState(fractionToPctClean(contract.td_share_pct));
  const [docPath, setDocPath] = useState(contract.model_document_path ?? "");
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    try {
      await update.mutateAsync({
        data_inicio: dataInicio,
        validade,
        success_fee_pct: pctToFraction(successFeePct),
        td_share_pct: pctToFraction(tdSharePct),
        model_document_path: docPath.trim() || null,
      });
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  return (
    <Modal
      onClose={onClose}
      title={`Editar contrato #${contract.id}`}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" disabled={update.isPending} onClick={() => void submit()}>
            {update.isPending ? "Salvando…" : "Salvar"}
          </Button>
        </>
      }
    >
      <div className="ctr-section" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {error && <p className="td-danger">{error}</p>}
        <label className="ctr-field">
          <span>Início da vigência</span>
          <input
            className="form-input"
            type="date"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
          />
        </label>
        <label className="ctr-field">
          <span>Validade</span>
          <input
            className="form-input"
            type="date"
            value={validade}
            onChange={(e) => setValidade(e.target.value)}
          />
        </label>
        <label className="ctr-field">
          <span>Success fee (%)</span>
          <input
            className="form-input"
            inputMode="decimal"
            value={successFeePct}
            onChange={(e) => setSuccessFeePct(e.target.value)}
          />
        </label>
        <label className="ctr-field">
          <span>Participação TD (%)</span>
          <input
            className="form-input"
            inputMode="decimal"
            value={tdSharePct}
            onChange={(e) => setTdSharePct(e.target.value)}
          />
        </label>
        <label className="ctr-field">
          <span>Documento modelo (path)</span>
          <input
            className="form-input"
            value={docPath}
            onChange={(e) => setDocPath(e.target.value)}
            placeholder="—"
          />
        </label>
      </div>
    </Modal>
  );
}

// ─── Status transitions ────────────────────────────────────────────────────

function StatusActions({
  workspaceId,
  contractId,
  status,
}: {
  workspaceId: number;
  contractId: number;
  status: ContractStatus;
}) {
  const transition = useTransitionContract(workspaceId, contractId);
  const [error, setError] = useState<string | null>(null);
  const next = NEXT_STATES[status];

  if (next.length === 0) return null;

  async function go(to: ContractStatus) {
    setError(null);
    try {
      await transition.mutateAsync(to);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  return (
    <Guard permission="contracts.activate">
      {next.map((to) => (
        <Button
          key={to}
          variant={to === "active" ? "primary" : to === "canceled" ? "danger" : "ghost"}
          disabled={transition.isPending}
          onClick={() => void go(to)}
        >
          {to === "active" ? "Ativar" : STATUS_LABEL[to]}
        </Button>
      ))}
      {error && <span className="td-danger">{error}</span>}
    </Guard>
  );
}

// ─── Visão ───────────────────────────────────────────────────────────────

function VisaoTab({
  contract,
  templateName,
}: {
  contract: import("@/types/contract").ContractDetail;
  templateName: string;
}) {
  const [recuperado, setRecuperado] = useState("100000");
  const cascade = useMemo(
    () =>
      computeCascade({
        recuperado: Number(recuperado) || 0,
        success_fee_pct: contract.success_fee_pct,
        td_share_pct: contract.td_share_pct,
        indicacoes: contract.indicacoes.map((i) => ({
          label: i.indicado_nome ?? `Indicado #${i.indicado_user_id}`,
          percent: i.percent,
          taxa_imposto_pct: i.taxa_imposto_pct,
        })),
      }),
    [recuperado, contract],
  );

  return (
    <div className="ctr-detail">
      <div className="ctr-terms">
        <div className="ctr-term">
          <span>Template de origem</span>
          <strong>{templateName}</strong>
        </div>
        <div className="ctr-term">
          <span>Operador</span>
          <strong>{OPERATOR_LABEL[contract.operator_type]}</strong>
        </div>
        <div className="ctr-term">
          <span>Success fee</span>
          <strong>{formatPct(contract.success_fee_pct)}</strong>
        </div>
        <div className="ctr-term">
          <span>Participação TD</span>
          <strong>{formatPct(contract.td_share_pct)}</strong>
        </div>
        <div className="ctr-term">
          <span>Vigência</span>
          <strong>
            {formatDate(contract.data_inicio)} – {formatDate(contract.validade)}
          </strong>
        </div>
        <div className="ctr-term">
          <span>Ativado em</span>
          <strong>{formatDate(contract.activated_at)}</strong>
        </div>
        <div className="ctr-term">
          <span>Documento modelo</span>
          <strong>{contract.model_document_path ?? "—"}</strong>
        </div>
      </div>

      <p className="ctr-section-title">Cascata (preview)</p>
      <label className="ctr-field" style={{ maxWidth: 280 }}>
        <span>Valor recuperado (exemplo)</span>
        <input
          type="number"
          min={0}
          value={recuperado}
          onChange={(e) => setRecuperado(e.target.value)}
        />
      </label>
      <CascadePreview cascade={cascade} />
    </div>
  );
}

// ─── Teses ───────────────────────────────────────────────────────────────

function TesesTab({
  workspaceId,
  contractId,
  links,
}: {
  workspaceId: number;
  contractId: number;
  links: ContractTeseLink[];
}) {
  const { data: teses = [] } = useTeses(true);
  const addTese = useAddContractTese(workspaceId, contractId);
  const removeTese = useRemoveContractTese(workspaceId, contractId);
  const [toAdd, setToAdd] = useState("");
  const [error, setError] = useState<string | null>(null);

  const linkedIds = new Set(links.map((l) => l.tese_id));
  const available = teses.filter((t) => !linkedIds.has(t.id));

  async function add() {
    if (!toAdd) return;
    setError(null);
    try {
      await addTese.mutateAsync(Number(toAdd));
      setToAdd("");
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  const columns: Column<ContractTeseLink>[] = [
    { id: "nome", header: "Tese", cell: (l) => <span className="razao">{l.tese_nome}</span> },
    { id: "slug", header: "Slug", cell: (l) => <span className="td-muted">{l.tese_slug}</span> },
  ];

  return (
    <div className="ctr-detail">
      <Guard permission="contracts.write">
        <div className="emp-toolbar">
          <label className="ctr-field" style={{ minWidth: 240 }}>
            <Select
              value={toAdd || null}
              onChange={setToAdd}
              options={available.map((t) => ({ value: String(t.id), label: t.nome }))}
              placeholder="Vincular tese…"
            />
          </label>
          <Button
            variant="primary"
            iconLeft="plus"
            disabled={!toAdd || addTese.isPending}
            onClick={() => void add()}
          >
            Vincular
          </Button>
        </div>
        {error && <p className="td-danger">{error}</p>}
      </Guard>

      <Table
        rows={links}
        columns={columns}
        getKey={(l) => String(l.id)}
        rowAction={(l) => (
          <Guard permission="contracts.write">
            <Button
              variant="ghost"
              size="sm"
              iconLeft="x"
              disabled={removeTese.isPending}
              onClick={() => void removeTese.mutateAsync(l.id)}
            />
          </Guard>
        )}
        empty={<EmptyState icon="file-text" title="Nenhuma tese vinculada" />}
      />
    </div>
  );
}

// ─── Indicações ────────────────────────────────────────────────────────────

function IndicacoesTab({
  workspaceId,
  contractId,
  indicacoes,
}: {
  workspaceId: number;
  contractId: number;
  indicacoes: ContractIndicacao[];
}) {
  const { data: members = [] } = useMembers(workspaceId);
  const add = useAddIndicacao(workspaceId, contractId);
  const update = useUpdateIndicacao(workspaceId, contractId);
  const remove = useRemoveIndicacao(workspaceId, contractId);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<ContractIndicacao | null>(null);

  const columns: Column<ContractIndicacao>[] = [
    {
      id: "nome",
      header: "Indicado",
      cell: (i) => <span className="razao">{i.indicado_nome ?? `#${i.indicado_user_id}`}</span>,
    },
    {
      id: "tipo",
      header: "Tipo",
      cell: (i) => <Pill tone={i.tipo === "interno" ? "diag" : "onb"}>{i.tipo}</Pill>,
    },
    { id: "percent", header: "%", align: "right", cell: (i) => formatPct(i.percent) },
    {
      id: "imposto",
      header: "Imposto",
      align: "right",
      cell: (i) => formatPct(i.taxa_imposto_pct),
    },
  ];

  return (
    <div className="ctr-detail">
      <Guard permission="contracts.write">
        <div className="emp-toolbar">
          <div className="emp-toolbar-spacer" />
          <Button variant="primary" iconLeft="plus" onClick={() => setAdding(true)}>
            Nova indicação
          </Button>
        </div>
      </Guard>

      <Table
        rows={indicacoes}
        columns={columns}
        getKey={(i) => String(i.id)}
        rowAction={(i) => (
          <Guard permission="contracts.write">
            <div className="ctr-row-actions">
              <Button variant="ghost" size="sm" iconLeft="settings" onClick={() => setEditing(i)} />
              <Button
                variant="ghost"
                size="sm"
                iconLeft="x"
                disabled={remove.isPending}
                onClick={() => void remove.mutateAsync(i.id)}
              />
            </div>
          </Guard>
        )}
        empty={<EmptyState icon="users" title="Nenhuma indicação" />}
      />

      {adding && (
        <IndicacaoModal
          mode="create"
          members={members}
          onClose={() => setAdding(false)}
          onSubmit={async (payload) => {
            await add.mutateAsync(payload);
            setAdding(false);
          }}
          pending={add.isPending}
        />
      )}
      {editing && (
        <IndicacaoModal
          mode="edit"
          indicacao={editing}
          onClose={() => setEditing(null)}
          onSubmit={async (payload) => {
            await update.mutateAsync({
              indId: editing.id,
              payload: {
                tipo: payload.tipo,
                percent: payload.percent,
                taxa_imposto_pct: payload.taxa_imposto_pct,
              },
            });
            setEditing(null);
          }}
          pending={update.isPending}
        />
      )}
    </div>
  );
}

type MemberLite = { user_id: number; user_name: string | null; user_email_masked: string };

// Campos compartilhados entre criação e edição de indicação (tipo + %s).
function IndicacaoFields({
  tipo,
  onTipo,
  percentPct,
  onPercent,
  taxaPct,
  onTaxa,
}: {
  tipo: IndicacaoTipo;
  onTipo: (v: IndicacaoTipo) => void;
  percentPct: string;
  onPercent: (v: string) => void;
  taxaPct: string;
  onTaxa: (v: string) => void;
}) {
  return (
    <>
      <label className="ctr-field">
        <span>Tipo</span>
        <Select<IndicacaoTipo>
          value={tipo}
          onChange={onTipo}
          options={[
            { value: "interno", label: "Interno" },
            { value: "externo", label: "Externo" },
          ]}
        />
      </label>
      <div className="ctr-field-row">
        <label className="ctr-field">
          <span>Percentual (%)</span>
          <input
            type="number"
            min={0}
            max={100}
            step="0.01"
            value={percentPct}
            onChange={(e) => onPercent(e.target.value)}
          />
        </label>
        <label className="ctr-field">
          <span>Imposto (%)</span>
          <input
            type="number"
            min={0}
            max={100}
            step="0.01"
            value={taxaPct}
            onChange={(e) => onTaxa(e.target.value)}
          />
        </label>
      </div>
    </>
  );
}

type IndicacaoPayload = {
  indicado_user_id: number;
  tipo: IndicacaoTipo;
  percent: string;
  taxa_imposto_pct: string;
};

type IndicacaoModalProps =
  | {
      mode: "create";
      members: MemberLite[];
      onClose: () => void;
      onSubmit: (payload: IndicacaoPayload) => Promise<void>;
      pending: boolean;
    }
  | {
      mode: "edit";
      indicacao: ContractIndicacao;
      onClose: () => void;
      onSubmit: (payload: IndicacaoPayload) => Promise<void>;
      pending: boolean;
    };

// Modal único de indicação. mode="create" mostra o select de indicado e usa
// defaults; mode="edit" prefilla a partir da indicação existente. O payload de
// edição reusa `indicado_user_id` da indicação (o backend ignora na atualização).
function IndicacaoModal(props: IndicacaoModalProps) {
  const { mode, onClose, onSubmit, pending } = props;
  const isEdit = mode === "edit";
  const existing = isEdit ? props.indicacao : null;

  const [userId, setUserId] = useState(existing ? String(existing.indicado_user_id) : "");
  const [tipo, setTipo] = useState<IndicacaoTipo>(existing ? existing.tipo : "interno");
  const [percentPct, setPercentPct] = useState(
    existing ? fractionToPctClean(existing.percent) : "",
  );
  const [taxaPct, setTaxaPct] = useState(
    existing ? fractionToPctClean(existing.taxa_imposto_pct) : "6.15",
  );
  const [error, setError] = useState<string | null>(null);

  const valid = isEdit ? true : userId.trim() !== "" && Number(percentPct) > 0;

  async function submit() {
    if (!valid) return;
    setError(null);
    try {
      await onSubmit({
        indicado_user_id: Number(userId),
        tipo,
        percent: pctToFraction(percentPct),
        taxa_imposto_pct: taxaPct.trim()
          ? pctToFraction(taxaPct)
          : isEdit
            ? "0"
            : "0.0615",
      });
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  return (
    <Modal
      onClose={onClose}
      title={
        isEdit && existing
          ? `Editar indicação · ${existing.indicado_nome ?? `#${existing.indicado_user_id}`}`
          : "Nova indicação"
      }
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" disabled={!valid || pending} onClick={() => void submit()}>
            {isEdit ? "Salvar" : "Adicionar"}
          </Button>
        </>
      }
    >
      <div className="ctr-section">
        {error && <p className="td-danger">{error}</p>}
        {mode === "create" && (
          <label className="ctr-field">
            <span>Indicado</span>
            <Select
              value={userId || null}
              onChange={setUserId}
              options={props.members.map((m) => ({
                value: String(m.user_id),
                label: m.user_name ?? m.user_email_masked,
              }))}
              placeholder="Selecione…"
            />
          </label>
        )}
        <IndicacaoFields
          tipo={tipo}
          onTipo={setTipo}
          percentPct={percentPct}
          onPercent={setPercentPct}
          taxaPct={taxaPct}
          onTaxa={setTaxaPct}
        />
      </div>
    </Modal>
  );
}

// ─── Assinatura ──────────────────────────────────────────────────────────

function AssinaturaTab({
  workspaceId,
  contract,
}: {
  workspaceId: number;
  contract: import("@/types/contract").ContractDetail;
}) {
  const attach = useAttachSignature(workspaceId, contract.id);
  const [method, setMethod] = useState<SignatureMethod>("manual");
  const [docPath, setDocPath] = useState("");
  const [error, setError] = useState<string | null>(null);

  const tone =
    contract.signature_status === "assinado"
      ? "ativa"
      : contract.signature_status === "invalido"
        ? "alerta"
        : "pausada";

  // Gov.br ainda não tem verificação implementada no backend (retorna 422);
  // mantemos a opção visível mas bloqueamos o submit até a integração existir.
  const govbrUnavailable = method === "govbr";

  async function submit() {
    if (!docPath.trim() || govbrUnavailable) return;
    setError(null);
    try {
      await attach.mutateAsync({ signature_method: method, signed_document_path: docPath.trim() });
      setDocPath("");
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  return (
    <div className="ctr-detail">
      <div className="ctr-terms">
        <div className="ctr-term">
          <span>Status</span>
          <strong>
            <Pill tone={tone}>{contract.signature_status}</Pill>
          </strong>
        </div>
        <div className="ctr-term">
          <span>Método</span>
          <strong>{contract.signature_method ?? "—"}</strong>
        </div>
        <div className="ctr-term">
          <span>Assinado em</span>
          <strong>{formatDate(contract.signed_at)}</strong>
        </div>
        <div className="ctr-term">
          <span>Signatário</span>
          <strong>{contract.signature_signer_name ?? "—"}</strong>
        </div>
        <div className="ctr-term">
          <span>Documento assinado</span>
          <strong>{contract.signed_document_path ?? "—"}</strong>
        </div>
        <div className="ctr-term">
          <span>Verificado</span>
          <strong>{contract.signature_verified ? "Sim" : "Não"}</strong>
        </div>
      </div>

      <Guard permission="contracts.write">
        <p className="ctr-section-title">Anexar assinatura</p>
        {error && <p className="td-danger">{error}</p>}
        <div className="ctr-field-row">
          <label className="ctr-field">
            <span>Método</span>
            <Select<SignatureMethod>
              value={method}
              onChange={setMethod}
              options={[
                { value: "manual", label: "Manual" },
                { value: "govbr", label: "Gov.br" },
              ]}
            />
          </label>
          <label className="ctr-field">
            <span>Caminho do PDF assinado</span>
            <input
              type="text"
              value={docPath}
              onChange={(e) => setDocPath(e.target.value)}
              placeholder="path/para/documento.pdf"
            />
          </label>
        </div>
        <div className="ctr-actions">
          <Button
            variant="primary"
            disabled={!docPath.trim() || govbrUnavailable || attach.isPending}
            onClick={() => void submit()}
          >
            Anexar assinatura
          </Button>
        </div>
        {method === "govbr" && (
          <p className="td-faded">Assinatura Gov.br ainda não disponível.</p>
        )}
      </Guard>
    </div>
  );
}
